import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { Link, useLocation, useNavigate } from "react-router-dom";

import Sitemap from "routes/sitemap";
import { join } from "../../../helpers/functions";
import useAPI from "../../../hooks/useAPI";
import Filters from "./Filters/Filters";
import PageLoadingBar from "components/Loading/PageLoadingBar";

import Icon from "@mui/material/Icon";
import "../../../assets/scss/variables.scss";
import scss from "./CatalogueAside.module.scss";
import { useTranslation } from "react-i18next";


/**
 * Show category tree.
 *
 * @param {[]} availableFilters The list of available filters.
 *
 * @return {JSX.Element}
 * @constructor
 */
const CatalogueAside = ({
    setActiveCategory = () => null,
    availableFilters,
    isFetched,
    setSelectedFilters,
    selectedFilters,
    changeFilters,
    setChangeFilters,
    setNewQuery = () => null,
    newQuery,
    categoryid,
    setCurrentPage,
    lastSelectedFilterKey,
    setLastSelectedFilterKey = () => {},
    translateKey,
}) => {
    const { t } = useTranslation();

    const api = useAPI();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [categoryTrail, setCategoryTrail] = useState([]);

    // Whether the aside is opened over the content, used while it is collapsed to the rail
    const [open, setOpen] = useState(false);

    // Load all categories
    const { data } = useQuery(["CatalogueAside.CategoryTree"], () => api.get("/categories/product/tree"));

    // Set the path for each category
    const setPath = (category, parent = null, depth = 0) => {
        category = {
            ...category,
            depth: depth,
            path: (parent?.path ?? "") + "/" + category?.slug,
        };

        // Recurse over children
        category.children = (category.children ?? []).map((child) => setPath(child, category, depth + 1));
        return category;
    };
    const categories = Array.isArray(data) ? data.map((category) => setPath(category)) : [];
    const selectedCategory = categoryTrail[categoryTrail.length - 1];
    const visibleCategories = selectedCategory?.children ?? categories;

    const openCategory = (category) => {
        setActiveCategory(category);
        setCategoryTrail([]);
        setOpen(false);
        navigate(`${Sitemap.catalogue.category}${category.path}`);
    };

    const selectCategory = (category) => {
        if (category.children?.length > 0) {
            setCategoryTrail((trail) => [...trail, category]);
            return;
        }

        openCategory(category);
    };

    useEffect(() => {
        let slugs = pathname.split("/");
        let cat = Array.isArray(data) ? data : [];
        for (const slug of slugs) {
            let tmp = cat.filter((item) => item?.slug === slug)[0]?.children;
            if (tmp) {
                cat = tmp;
            }
        }
        let active = cat.filter((item) => item?.slug === slugs[slugs.length - 1])[0];

        if (active != null && pathname !== Sitemap.catalogue._) {
            setActiveCategory(active);
        }
        if (pathname === Sitemap.home) {
            setActiveCategory(null);
        }
        setCategoryTrail([]);
        setOpen(false);
    }, [pathname]);
    const showCategoryNavigation = ["/katalog/kategorija", "/novo", "/promo", "/lista-zelja", "/akcija"].some((path) => pathname.includes(path));
    const showSearchTitle = pathname.includes("/katalog/pretraga");

    const showFiltersPanel = !selectedCategory && ["/katalog/kategorija", "/katalog/pretraga", "/novo"].some((path) => pathname.includes(path));
    const clearFilters = () => {
        setSelectedFilters([]);
        setChangeFilters(true);
        setNewQuery();
        if (newQuery) {
            window.history.replaceState(null, "", `/katalog/kategorija/${categoryid}`);
        }
    };
    const clearFiltersButton = showFiltersPanel && (
        <button type="button" className={join([scss.allCategoryButton, scss.clearFiltersButton])} onClick={clearFilters} aria-label={t(`${translateKey}-text-clear_filters`)} title={t(`${translateKey}-text-clear_filters`)}>
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="m5 5 10 10M15 5 5 15" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
        </button>
    );

    return (
        <>
            {/* Collapsed aside */}
            <button type="button" className={scss.rail} onClick={() => setOpen(true)} aria-label={t(`${translateKey}-title`)} aria-expanded={open}>
                <Icon>menu</Icon>
            </button>
            {open && <div className={scss.backdrop} onClick={() => setOpen(false)} />}

            <div className={join([scss.wrapper, open && scss.open])}>
                <button type="button" className={scss.close} onClick={() => setOpen(false)} aria-label="close">
                    <Icon>close</Icon>
                </button>
                {selectedCategory ? (
                    <div className={scss.drillHeader}>
                        <div className={scss.backRow}>
                            <button type="button" className={scss.backButton} onClick={() => setCategoryTrail((trail) => trail.slice(0, -1))}>
                                <span className={scss.backIcon}>
                                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                        <path d="m12.5 4.5-5.5 5.5 5.5 5.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                                <span className={scss.backText}>Nazad</span>
                            </button>
                            <button type="button" className={scss.allCategoryButton} onClick={() => openCategory(selectedCategory)} aria-label={`Svi proizvodi iz kategorije ${selectedCategory.name}`}>
                                Sve
                            </button>
                        </div>
                        <div className={scss.currentCategory}>{selectedCategory.name}</div>
                    </div>
                ) : (
                    <>
                        {/* Top categories */}
                        {showCategoryNavigation ? (
                            <div className={scss.backRow}>
                                <Link className={scss.backButton} to={Sitemap.home}>
                                    <span className={scss.backIcon}>
                                        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                            <path d="m12.5 4.5-5.5 5.5 5.5 5.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                    <span className={scss.backText}>Nazad</span>
                                </Link>
                                {clearFiltersButton}
                            </div>
                        ) : (
                            <div className={join([scss.label, !showSearchTitle && scss.categoriesLabel, showSearchTitle && scss.searchHeader])}>
                                {showSearchTitle ? t(`${translateKey}-text-search`) : "Kategorije"}
                                {showSearchTitle && clearFiltersButton}
                            </div>
                        )}

                    </>
                )}
                {!showFiltersPanel ? (
                    <ul key={selectedCategory?.id ?? "root"} className={`no-padding ${scss.items}`}>
                        {visibleCategories.map((item) => (
                            <li key={item.id} className={scss.list}>
                                <button type="button" className={scss.superLink} onClick={() => selectCategory(item)}>
                                    {item.icon && <img className={scss.icon} src={item.icon} alt="" />}
                                    <span>{item.name}</span>
                                    {item.children?.length > 0 && (
                                        <svg className={scss.nextIcon} viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                            <path d="m7.5 4.5 5.5 5.5-5.5 5.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                </button>
                            </li>
                        ))}
                    </ul>
                ) : isFetched ? (
                    <Filters
                        changeFilters={changeFilters}
                        setLastSelectedFilterKey={setLastSelectedFilterKey}
                        lastSelectedFilterKey={lastSelectedFilterKey}
                        setChangeFilters={setChangeFilters}
                        selectedFilters={selectedFilters}
                        setSelectedFilters={setSelectedFilters}
                        availableFilters={availableFilters}
                        isLoaded={isFetched}
                        setCurrentPage={setCurrentPage}
                    />
                ) : (
                    <PageLoadingBar fullScreen={false} />
                )}
            </div>
        </>
    );
};

export default CatalogueAside;
